<script lang="ts">
  import { onDestroy } from 'svelte';
  import type { AppContext } from '../app';
  import { formatUtc } from '../core/time';
  import { lang, t } from '../i18n';
  import { skipSlowmo, slowmoActive } from '../state/playback';
  import { cue } from '../audio/sound';
  import { SPEEDS, currentDate, dockExpanded, playback, speedIndex, togglePlay, toggleReverse } from '../state/stores';
  import Icon from './icons/Icon.svelte';
  import Throttle from './Throttle.svelte';
  import Timeline from './Timeline.svelte';

  // The dock (S2-05, S3-15): play key, date and speed, the mission timeline (it replaces the Sprint 2 scrubber and
  // its quick-jump chips: the selected mission's events are ticks on it) and the time-warp throttle. On wide screens
  // the controls sit in one compact row above the timeline (Sprint 2 parking lot). On phones (S4-15) the dock is one
  // row above a slim timeline; the lanes, the zoom row and the throttle open with the expand key.
  let { ctx }: { ctx: AppContext } = $props();

  // The date label is throttled to ~10 Hz (S1-11, risk table); the scrubber follows every change.
  let shownDate = $state(0);
  let last = 0;
  let timer: ReturnType<typeof setTimeout> | undefined;
  const unsubscribe = currentDate.subscribe((ms) => {
    const now = performance.now();
    clearTimeout(timer);
    if (now - last > 100) {
      last = now;
      shownDate = ms;
    } else {
      timer = setTimeout(() => (shownDate = ms), 100);
    }
  });
  onDestroy(() => {
    unsubscribe();
    clearTimeout(timer);
  });

  // re-evaluated with every date change and play/pause; slowmoActive is false while paused
  const slowmo = $derived($playback.playing && slowmoActive($currentDate));

  const speedId = $derived(SPEEDS[speedIndex($playback.speed)]!.id);

  // Long-press on the play key flips the direction (S2-21); the click that ends a long press is swallowed.
  const LONG_PRESS_MS = 550;
  let pressTimer: ReturnType<typeof setTimeout> | undefined;
  let longPressed = false;
  function startLongPress(): void {
    longPressed = false;
    clearTimeout(pressTimer);
    pressTimer = setTimeout(() => {
      longPressed = true;
      toggleReverse();
      cue('detent');
    }, LONG_PRESS_MS);
  }
  function cancelLongPress(): void {
    clearTimeout(pressTimer);
  }
  function onPlayClick(): void {
    if (longPressed) {
      longPressed = false;
      return;
    }
    togglePlay();
  }
</script>

<section class="panel time" aria-label={$t('time.controls')}>
  <div class="row">
    <button
      type="button"
      class="play"
      class:reverse={$playback.reverse}
      aria-label={$playback.playing ? $t('time.pause') : $playback.reverse ? $t('time.playReverse') : $t('time.play')}
      title="{$playback.playing ? $t('time.pause') : $t('time.play')} (Space) · {$t('time.reverse')}: long press / R"
      onpointerdown={startLongPress}
      onpointerup={cancelLongPress}
      onpointerleave={cancelLongPress}
      onclick={onPlayClick}
    >
      <Icon name={$playback.playing ? 'pause' : 'play'} size={22} />
    </button>
    <output class="date" aria-live="off" data-testid="date">{formatUtc(shownDate, $lang)}</output>
    {#if slowmo}
      <button
        type="button"
        class="slowmo"
        aria-label={$t('time.slowmoSkip')}
        title={$t('time.slowmoSkip')}
        onclick={() => skipSlowmo($currentDate)}>{$t('time.slowmo')}</button
      >
    {/if}
    <span class="speed" data-testid="speed"
      >{#if $playback.reverse}<span aria-label={$t('time.reverse')}>◀ </span>{/if}{$t(`speed.${speedId}`)}</span
    >
    <div class="wide-throttle"><Throttle /></div>
    <button
      type="button"
      class="more"
      aria-expanded={$dockExpanded}
      aria-label={$dockExpanded ? $t('timeline.collapse') : $t('timeline.expand')}
      title={$dockExpanded ? $t('timeline.collapse') : $t('timeline.expand')}
      onclick={() => dockExpanded.update((v) => !v)}
    >
      <span class="chev" class:up={!$dockExpanded}><Icon name="chevron" size={16} /></span>
    </button>
  </div>
  <Timeline {ctx} expanded={$dockExpanded} />
  {#if $dockExpanded}<div class="narrow-throttle"><Throttle /></div>{/if}
</section>

<style>
  .time {
    width: 100%;
    max-width: 760px;
    padding: 10px 12px 10px;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    gap: 4px;
  }
  .row {
    display: flex;
    align-items: center;
    gap: 12px;
  }
  /* Chunky orange key with a hard red offset shadow; it "presses in" when active. */
  .play {
    flex: none;
    width: 52px;
    height: 52px;
    margin: 0 4px 4px 0;
    display: grid;
    place-items: center;
    background: var(--accent);
    color: var(--on-accent);
    box-shadow: var(--press-shadow);
    transition:
      transform var(--dur-fast) var(--ease),
      box-shadow var(--dur-fast) var(--ease);
  }
  /* backwards: the play triangle points left */
  .play.reverse :global(svg) {
    transform: scaleX(-1);
  }
  .play:active {
    transform: translate(3px, 3px);
    box-shadow: 1px 1px 0 var(--red);
  }
  .date {
    flex: 1;
    min-width: 0;
    font-family: var(--font-mono);
    font-variant-numeric: tabular-nums;
    font-size: 1.1rem;
    font-weight: 600;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  /* SLOW-MO badge (S2-11): signal red; one tap skips the slow-motion of this flyby */
  .slowmo {
    flex: none;
    position: relative;
    min-height: 26px;
    padding: 0 8px;
    background: var(--signal);
    color: var(--on-signal);
    font-size: 0.7rem;
    font-weight: 700;
    letter-spacing: var(--track-caps);
    text-transform: uppercase;
    animation: blink 1.2s steps(2, jump-none) infinite;
  }
  .slowmo::after {
    content: '';
    position: absolute;
    inset: -9px 0;
  }
  @keyframes blink {
    50% {
      opacity: 0.65;
    }
  }
  @media (prefers-reduced-motion: reduce) {
    .slowmo {
      animation: none;
    }
  }
  .speed {
    flex: none;
    font-family: var(--font-mono);
    font-size: 0.85rem;
    font-weight: 600;
    color: var(--accent);
    white-space: nowrap;
  }
  .more {
    flex: none;
    width: var(--touch);
    height: var(--touch);
    display: grid;
    place-items: center;
    border: var(--rule-w) solid var(--rule);
  }
  .more:hover {
    border-color: var(--accent);
    color: var(--accent);
  }
  .chev {
    display: grid;
    transform: rotate(90deg);
  }
  .chev.up {
    transform: rotate(-90deg);
  }
  /* phones (S4-15): one slim row — a 44 px play key, less padding */
  @media (max-width: 899px) {
    .time {
      padding: 4px 8px;
      gap: 2px;
    }
    .play {
      width: var(--touch);
      height: var(--touch);
      margin: 0 3px 3px 0;
    }
  }
  @media (max-width: 480px) {
    .row {
      gap: 10px;
    }
    .date {
      font-size: 0.95rem;
      letter-spacing: -0.02em;
    }
    .speed {
      font-size: 0.75rem;
    }
  }
  .wide-throttle {
    display: none;
  }
  /* wide screens: one compact row (play, date, speed, throttle) above the timeline */
  @media (min-width: 900px) {
    .time {
      max-width: 1100px;
    }
    .wide-throttle {
      display: block;
      flex: 0 1 380px;
    }
    .narrow-throttle {
      display: none;
    }
    .more {
      display: none;
    }
  }
</style>
